import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "good_fruit_token";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setAuth = (newToken, newUser) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
    }
    if (newUser) setUser(newUser);
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const authHeaders = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const refreshUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      setLoading(false);
      return res.data;
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
      setLoading(false);
      return null;
    }
  }, [token]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const register = async ({ name, email, password }) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    setAuth(res.data.token, res.data.user);
    return res.data.user;
  };

  const login = async ({ email, password }) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setAuth(res.data.token, res.data.user);
    return res.data.user;
  };

  const createChurch = async (payload) => {
    const res = await axios.post(`${API}/churches`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await refreshUser();
    return res.data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        register,
        login,
        logout,
        refreshUser,
        createChurch,
        setAuth,
        authHeaders,
        API,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
